import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { trackBuyClick } from "@/lib/track";
import { ProductGallery } from "@/components/ProductGallery";
import { ComparisonTable } from "@/components/ComparisonTable";
import { AtAGlance } from "@/components/AtAGlance";
import { autoLabels } from "@/lib/badges";
import { SEO } from "@/components/SEO";
import { ArrowRight } from "@phosphor-icons/react";

export function ListsPage() {
  const [lists, setLists] = useState([]);
  useEffect(() => {
    api.get("/lists").then((r) => setLists(r.data));
  }, []);

  return (
    <div className="max-w-7xl mx-auto px-6 lg:px-10 py-16">
      <SEO
        title="Curated lists"
        description="Editor-tested curated lists of the best home & kitchen products on Amazon.in — mixer grinders, air fryers, cookware, bedding, and more."
        path="/lists"
      />
      <p className="text-xs uppercase tracking-[0.2em] text-[#5B7B53] mb-3">Reviews & recommendations</p>
      <h1 className="font-display text-5xl text-[#2C2B29] mb-3">Curated lists</h1>
      <p className="text-lg text-[#5C5A56] max-w-2xl mb-12">
        Every list below is written by our editors after hours (sometimes weeks) of hands-on testing.
      </p>

      <div className="grid md:grid-cols-2 gap-8">
        {lists.map((l, idx) => (
          <Link
            key={l.id}
            to={`/list/${l.id}`}
            data-testid={`list-tile-${l.id}`}
            className={`hh-card rounded-3xl overflow-hidden group ${idx % 3 === 0 ? "md:col-span-2" : ""}`}
          >
            <div className={`overflow-hidden ${idx % 3 === 0 ? "aspect-[21/9]" : "aspect-[16/10]"}`}>
              <img src={l.hero_image} alt={l.title} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.03]" />
            </div>
            <div className="p-8">
              <span className="text-xs uppercase tracking-wider text-[#5B7B53]">{l.category}</span>
              <h2 className="font-display text-3xl text-[#2C2B29] mt-2 leading-tight group-hover:text-[#5B7B53] transition-colors">
                {l.title}
              </h2>
              <p className="text-sm text-[#5C5A56] mt-3">{l.subtitle}</p>
              <span className="inline-flex items-center gap-1.5 mt-4 text-sm font-medium text-[#D17A58]">
                Read the list <ArrowRight size={14} weight="bold" />
              </span>
            </div>
          </Link>
        ))}
      </div>
    </div>
  );
}

export function ListDetailPage() {
  const { id } = useParams();
  const [list, setList] = useState(null);

  useEffect(() => {
    api.get(`/lists/${id}`).then((r) => setList(r.data)).catch(() => setList(false));
  }, [id]);

  if (list === false) return <div className="max-w-3xl mx-auto p-10 text-[#5C5A56]">List not found.</div>;
  if (!list) return <div className="max-w-3xl mx-auto p-10 text-[#5C5A56]">Loading...</div>;

  const notes = list.item_notes || {};
  const labels = autoLabels(list.products, Object.fromEntries(Object.entries(notes).map(([k, v]) => [k, v?.best_for]).filter(([, v]) => v)));

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "ItemList",
    name: list.title,
    description: list.subtitle || list.intro,
    itemListElement: (list.products || []).map((p, i) => ({
      "@type": "ListItem",
      position: i + 1,
      name: p.title,
      image: p.image_url,
      url: p.affiliate_url,
    })),
  };

  return (
    <article>
      <SEO
        title={list.title}
        description={list.subtitle || list.intro?.slice(0, 155)}
        image={list.hero_image}
        path={`/list/${list.id}`}
        type="article"
        jsonLd={jsonLd}
      />
      <section className="relative overflow-hidden">
        <div className="absolute inset-0">
          <img src={list.hero_image} alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-b from-[#2C2B29]/40 via-[#2C2B29]/70 to-[#2C2B29]/95" />
        </div>
        <div className="relative max-w-4xl mx-auto px-6 lg:px-10 pt-32 pb-24 text-white">
          <p className="text-xs uppercase tracking-[0.2em] text-[#EADDC8] mb-4">{list.category}</p>
          <h1 data-testid="list-title" className="font-display text-5xl sm:text-6xl lg:text-7xl leading-[1.05] tracking-tight">
            {list.title}
          </h1>
          <p className="mt-6 text-xl text-[#EADDC8] max-w-2xl">{list.subtitle}</p>
        </div>
      </section>

      <section className="max-w-3xl mx-auto px-6 lg:px-10 py-10 sm:py-16">
        <p className="text-lg leading-relaxed text-[#2C2B29]">{list.intro}</p>
      </section>

      <AtAGlance products={list.products} labels={labels} />

      <section className="max-w-6xl mx-auto px-6 lg:px-10 pb-12 space-y-8">
        {list.products.length === 0 ? (
          <p className="text-[#5C5A56]">No products added to this list yet.</p>
        ) : (
          list.products.map((p, idx) => (
            <div key={p.id} id={`item-${p.id}`} className="scroll-mt-24 grid md:grid-cols-5 gap-6 hh-card rounded-3xl p-5 sm:p-6 md:p-8">
              <div className="md:col-span-2 relative">
                <div className="absolute top-3 left-3 z-10 bg-[#2C2B29] text-white text-xs font-medium px-2.5 py-1 rounded-full">#{idx + 1}</div>
                <ProductGallery product={p} idPrefix={`list-${p.id}-`} />
              </div>
              <div className="md:col-span-3 flex flex-col">
                {labels[p.id] && (
                  <span data-testid={`list-best-for-${p.id}`} className="self-start text-[11px] font-bold uppercase tracking-wide bg-[#D17A58]/10 text-[#B5613F] px-2.5 py-1 rounded-full mb-2">{labels[p.id]}</span>
                )}
                <span className="text-xs uppercase tracking-wider text-[#5B7B53]">{p.category}</span>
                <h3 className="font-display text-2xl sm:text-3xl text-[#2C2B29] mt-1 leading-tight">{p.title}</h3>
                <p className="text-[#5C5A56] mt-3">{p.long_description || p.short_description}</p>
                {notes[p.id]?.verdict && (
                  <p data-testid={`list-verdict-${p.id}`} className="mt-4 border-l-4 border-[#5B7B53] pl-4 text-[#2C2B29] italic">
                    <b className="not-italic">Our verdict: </b>{notes[p.id].verdict}
                  </p>
                )}

                {(p.pros?.length || p.cons?.length) && (
                  <div className="grid sm:grid-cols-2 gap-4 mt-5 text-sm">
                    {p.pros?.length > 0 && (
                      <div>
                        <p className="text-xs uppercase tracking-wider text-[#5B7B53] mb-2">Pros</p>
                        <ul className="space-y-1.5">
                          {p.pros.map((x) => <li key={x} className="text-[#2C2B29]">• {x}</li>)}
                        </ul>
                      </div>
                    )}
                    {p.cons?.length > 0 && (
                      <div>
                        <p className="text-xs uppercase tracking-wider text-[#D17A58] mb-2">Cons</p>
                        <ul className="space-y-1.5">
                          {p.cons.map((x) => <li key={x} className="text-[#5C5A56]">• {x}</li>)}
                        </ul>
                      </div>
                    )}
                  </div>
                )}

                <div className="mt-auto pt-6 flex items-center justify-between gap-4 flex-wrap">
                  <div>
                    <p className="text-2xl font-semibold text-[#2C2B29]">{p.price}</p>
                    <p className="text-xs text-[#5C5A56]">{p.rating}★ · {p.review_count.toLocaleString()} reviews</p>
                  </div>
                  <a
                    data-testid={`list-buy-${p.id}`}
                    href={p.affiliate_url}
                    target="_blank"
                    rel="noopener noreferrer nofollow sponsored"
                    onClick={() => trackBuyClick(p.id)}
                    className="hh-btn-primary rounded-full px-6 py-3 text-sm font-semibold"
                  >
                    Buy Now on Amazon →
                  </a>
                </div>
              </div>
            </div>
          ))
        )}
      </section>
      <div className="max-w-6xl mx-auto px-6 lg:px-10 pb-24">
        <ComparisonTable products={list.products} labels={labels} testid="list-comparison-table" />
      </div>
    </article>
  );
}
